<template>
  <!-- `blur` does not bubble out of the combobox's inner input, so leaving the cell is caught on
       focusout instead — the same "user has visited this field" signal the sibling cells get from
       @blur, without which an emptied beneficiary name never turns red. -->
  <div v-if="offersSuggestions" @focusout="$emit('touch')">
    <SearchableSelectComponent
      :options="options"
      :modelValue="model"
      allowFreeText
      size="sm"
      triggerIcon="fas fa-chevron-down"
      :invalid="isInvalid"
      :placeholder="$t('go_cancel.beneficiary_suggestions.placeholder')"
      @queryUpdated="model = $event"
      @update:modelValue="selectSuggestion"
    />
  </div>
  <input
    v-else
    v-model="model"
    type="text"
    class="form-control form-control-sm"
    :class="{ 'is-invalid': isInvalid }"
    :placeholder="$t('go_cancel.form.beneficiary_name.placeholder')"
    :disabled="disabled"
    @blur="emit('touch')"
  />
</template>

<script setup lang="ts">
import SearchableSelectComponent from "@/components/common/SearchableSelectComponent.vue";
import type { CancelBeneficiarySuggestion } from "@/dashboard/inventory_management/cancel/cancelBeneficiariesApi";
import { useCancellationTargets } from "@/dashboard/inventory_management/cancel/useCancellationTargetsStore";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

const props = withDefaults(
  defineProps<{
    suggestions?: CancelBeneficiarySuggestion[];
    touched?: boolean;
    disabled?: boolean;
  }>(),
  { suggestions: () => [], touched: false, disabled: false }
);

const emit = defineEmits<{
  select: [suggestion: CancelBeneficiarySuggestion];
  touch: [];
}>();

const model = defineModel<string>({ required: true });

const { t } = useI18n();
const targets = useCancellationTargets();

// A disabled combobox would be a worse plain input, so the read-only mode keeps the plain input.
const offersSuggestions = computed(
  () => props.suggestions.length > 0 && !props.disabled
);

const isInvalid = computed(() => props.touched && !model.value.trim());

const options = computed(() =>
  props.suggestions.map((suggestion) => ({
    value: suggestion.beneficiary_name,
    label: suggestion.beneficiary_name,
    description: [
      // Null when history records no type; the description omits it rather than guessing one.
      suggestion.beneficiary_type &&
        t(`go_cancel.form.beneficiary_type.${suggestion.beneficiary_type}`),
      suggestion.registry_code,
      suggestion.registry_country_region &&
        targets.value.label(suggestion.registry_country_region, t),
      suggestion.address,
    ]
      .filter(Boolean)
      .join(" · "),
  }))
);

/** Free text arrives via `queryUpdated`; only an actual option pick is a selection. */
const selectSuggestion = (beneficiaryName?: string) => {
  const suggestion = props.suggestions.find(
    (candidate) => candidate.beneficiary_name === beneficiaryName
  );
  if (suggestion) {
    emit("select", suggestion);
  }
};
</script>
